import type {
	ConstructionSiteDocument,
	GFADocument,
	ReferenceFilterValue,
} from "@dune/types";
import {
	Button,
	Input,
	PaginatedDataTable,
	Tabs,
	TabsList,
	TabsTrigger,
	useDebounce,
} from "@dune/ui";
import { Plus, SearchSm } from "@untitled-ui/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import { useGetConstructionSiteDocuments } from "../../../../api/queries/constructionSites.queries";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { useDeleteConstructionSiteDocumentMutation } from "../../../../api/mutations/constructionSites.mutations";
import { useDocumentStore } from "../../../../stores/document.store";
import { usePaginationStore } from "../../../../stores/pagination.store";
import ConfirmationDialog from "../../../../component/common/ConfirmationDialog";
import UploadDocumentModal from "./UploadDocumentModal";
import { documentsColumns } from "./documentsColumns";
import { useMe } from "../../../../api/queries/users.queries";
import { useGetGFADocuments } from "../../../../api/queries/gfa.queries";
import { useUnlinkFileFromGFA } from "../../../../api/mutations/gfa.mutations";
import GenerateGFAConstructionSiteDocumentsModal from "../../../gfa/components/modals/GenerateGFAConstructionSiteDocumentsModal";
import { FileText } from "lucide-react";
import { RoleEnum } from "../../../../../../../packages/vars/src";

interface DocumentsTabProps {
	constructionSiteId: string;
	isGFA?: boolean;
}

const SEARCH_DEBOUNCE_DELAY_MS = 500;

const DocumentsTab: React.FC<DocumentsTabProps> = ({
	constructionSiteId,
	isGFA = false,
}) => {
	const selectDocument = useDocumentStore((state) => state.selectDocument);
	const storedLimit = usePaginationStore((s) => s.limit);
	const setStoredLimit = usePaginationStore((s) => s.setLimit);

	const [pagination, setPagination] = useState({
		page: 1,
		limit: storedLimit,
	});

	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_DELAY_MS);

	const [referenceFilter, setReferenceFilter] =
		useState<ReferenceFilterValue>("all");

	useEffect(() => {
		void debouncedSearch;
		void referenceFilter;
		setPagination((currentPagination) => ({
			...currentPagination,
			page: 1,
		}));
	}, [debouncedSearch, referenceFilter]);

	const isReferencedParam =
		referenceFilter === "all" ? undefined : referenceFilter === "referenced";

	const paginationWithSearch = {
		...pagination,
		search: debouncedSearch,
		...(isGFA || isReferencedParam === undefined
			? {}
			: { isReferenced: isReferencedParam }),
	};

	const {
		data: constructionSiteDocuments,
		isLoading: isLoadingConstructionSiteDocuments,
	} = useGetConstructionSiteDocuments(
		isGFA ? 0 : Number(constructionSiteId),
		paginationWithSearch,
	);

	const { data: gfaDocuments, isLoading: isLoadingGFADocuments } =
		useGetGFADocuments(
			isGFA ? Number(constructionSiteId) : 0,
			paginationWithSearch,
		);

	const data = isGFA ? gfaDocuments : constructionSiteDocuments;

	const documents = data?.data || [];
	const meta = data?.meta || {
		page: 1,
		limit: pagination.limit,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);
	const [downloadingDocumentId, setDownloadingDocumentId] = useState<
		string | null
	>(null);
	const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
		null,
	);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isGenerateDocumentsModalOpen, setIsGenerateDocumentsModalOpen] =
		useState(false);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<
		ConstructionSiteDocument | GFADocument | null
	>(null);

	const deleteDocumentMutation = useDeleteConstructionSiteDocumentMutation(
		constructionSiteId,
		{
			onMutate: (documentId) => {
				setDeletingDocumentId(documentId);
			},
			onSettled: () => {
				setDeletingDocumentId(null);
			},
		},
	);

	const deleteGFADocumentMutation = useUnlinkFileFromGFA(
		isGFA ? Number(constructionSiteId) : 0,
		{
			onMutate: (documentId) => {
				setDeletingDocumentId(documentId);
			},
			onSettled: () => {
				setDeletingDocumentId(null);
			},
			showToast: true,
			invalidateDocuments: true,
		},
	);

	const handleDocumentClick = useCallback(
		async (doc: ConstructionSiteDocument | GFADocument) => {
			const docId = doc.id.toString();
			if (loadingDocumentId === docId) return;

			if (!doc.key || doc.key.trim() === "") {
				return;
			}

			setLoadingDocumentId(docId);

			try {
				const url = await getDocumentUrl({
					bucket: doc.bucket,
					key: doc.key,
				});

				if (url) {
					selectDocument(url, docId, doc.mimeType, doc.name);
				}
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'URL du document:",
					error,
				);
			} finally {
				setLoadingDocumentId(null);
			}
		},
		[loadingDocumentId, selectDocument],
	);

	const handleDeleteClick = useCallback(
		(documentId: string) => {
			const document = documents.find(
				(doc) => doc.id.toString() === documentId,
			);
			if (document) {
				setDocumentToDelete(document);
				setIsDeleteConfirmationOpen(true);
			}
		},
		[documents],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (documentToDelete) {
			if (isGFA) {
				await deleteGFADocumentMutation.mutateAsync(
					documentToDelete.id.toString(),
				);
			} else {
				await deleteDocumentMutation.mutateAsync(
					documentToDelete.id.toString(),
				);
			}
			setIsDeleteConfirmationOpen(false);
			setDocumentToDelete(null);
		}
	}, [
		documentToDelete,
		deleteDocumentMutation,
		deleteGFADocumentMutation,
		isGFA,
	]);
	const { me } = useMe();
	const isDuneUser = me?.role === "DUNE";
	const isInsurer = me?.role === RoleEnum.INSURER;
	const columns = documentsColumns({
		constructionSiteId: Number(constructionSiteId),
		loadingDocumentId,
		downloadingDocumentId,
		setDownloadingDocumentId,
		onDelete: isDuneUser ? handleDeleteClick : undefined,
		deletingDocumentId,
		isDuneUser,
		isGFA,
	});

	return (
		<div className="h-full">
			<div className="flex justify-between items-center mb-4">
				<div className="flex items-center gap-3 flex-wrap">
					<h2 className="text-text-main-blue font-semibold">Documents</h2>
					{!isGFA && (
						<Tabs
							value={referenceFilter}
							onValueChange={(value) =>
								setReferenceFilter(value as ReferenceFilterValue)
							}
						>
							<TabsList>
								<TabsTrigger value="all">Tous</TabsTrigger>
								<TabsTrigger value="referenced">Référencé</TabsTrigger>
								<TabsTrigger value="unreferenced">Non référencé</TabsTrigger>
							</TabsList>
						</Tabs>
					)}
					<div className="w-80">
						<Input
							type="text"
							placeholder="Rechercher un document"
							value={searchInput}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
								setSearchInput(event.target.value)
							}
							iconOrText={<SearchSm />}
							iconOrTextPosition="right"
						/>
					</div>
				</div>
				{!isInsurer && (
					<div className="flex gap-2">
						{isGFA && (
							<Button
								variant="outline"
								onClick={() => setIsGenerateDocumentsModalOpen(true)}
							>
								<FileText /> Générer des documents
							</Button>
						)}
						<Button onClick={() => setIsUploadModalOpen(true)}>
							<Plus /> Nouveau
						</Button>
					</div>
				)}
			</div>
			<PaginatedDataTable
				data={documents}
				columns={columns}
				noResultsText="Aucun document trouvé"
				isLoading={isLoadingConstructionSiteDocuments || isLoadingGFADocuments}
				variant="static-clean"
				onRowClick={handleDocumentClick}
				meta={meta}
				onPageChange={(page) => {
					setPagination({ ...pagination, page });
				}}
				onLimitChange={(newLimit) => {
					setStoredLimit(newLimit);
					setPagination({ ...pagination, limit: newLimit, page: 1 });
				}}
			/>
			{isGFA && (
				<GenerateGFAConstructionSiteDocumentsModal
					types={[
						"DEVIS_GFA",
						"RAPPORT_EXPERTISE_GFA",
						"INDICATION_TARIFAIRE_GFA_CARAVELA",
					]}
					open={isGenerateDocumentsModalOpen}
					onOpenChange={setIsGenerateDocumentsModalOpen}
					gfaConstructionSiteId={Number(constructionSiteId)}
					onSuccess={() => {
						setIsGenerateDocumentsModalOpen(false);
					}}
				/>
			)}
			<UploadDocumentModal
				open={isUploadModalOpen}
				onOpenChange={setIsUploadModalOpen}
				constructionSiteId={Number(constructionSiteId)}
				isGFA={isGFA}
			/>
			<ConfirmationDialog
				open={isDeleteConfirmationOpen}
				onOpenChange={setIsDeleteConfirmationOpen}
				title="Supprimer le document"
				description={`Êtes-vous sûr de vouloir supprimer le document "${documentToDelete?.name}" ? Cette action est irréversible.`}
				confirmText="Supprimer"
				cancelText="Annuler"
				variant="danger"
				onConfirm={handleConfirmDelete}
				isLoading={
					isGFA
						? deleteGFADocumentMutation.isPending
						: deleteDocumentMutation.isPending
				}
			/>
		</div>
	);
};

export default DocumentsTab;
